<template>
  <Swiper :allowTouchMove="false" @swiper="(swiper) => (swiperClass = swiper)">
    <SwiperSlide>
      <div class="space-y-4 sm:space-y-6">
        <h2 class="text-lg sm:text-2xl font-medium text-center">
          Support
          <span class="text-nami-comi-blue">
            {{ orgName }}
          </span>
        </h2>
        <div
          v-if="tiers && tiers.length === 0"
          class="text-center space-y-4 sm:space-y-6"
        >
          <AsyncImage
            :src="getAbsoluteAssetLink('nami/stickers/empty.png')"
            class="max-w-[16rem] w-full mx-auto"
          />
          <p class="text-sm sm:text-base">
            {{ $t("components.organizationSubscribeForm.noTiersText") }}
          </p>
        </div>
        <template v-else>
          <h3 class="text-center text-sm sm:text-base">
            {{ $t("components.organizationSubscribeForm.tierSelectText") }}
          </h3>
          <ul v-if="pending">
            <li v-for="_ in Array(3)">
              <Skeleton class="h-32" />
            </li>
          </ul>
          <ul
            v-else
            class="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 py-4"
          >
            <li v-for="tier in tiers">
              <SubscriptionTierCard
                class="h-full"
                :orgSlug
                :id="tier.id"
                :title="tier.title"
                :price="tier.price / 100"
                :features="tier.features"
                :highlighted="tier.highlighted || highlightTierId === tier.id"
                :highlightText="highlightText"
                :emotes="tier.emotes"
                :frames="tier.frames"
                :banner="tier.banner"
                :subscribedToId="tier.subscribedToId"
                :disabled="disabled"
                @select="
                  () => {
                    selectedTier = tier;
                    selectedPledgeAmount = tier.price;
                    swiperClass?.slideTo(1);
                    scrollUp();
                  }
                "
              />
            </li>
          </ul>
        </template>
      </div>
    </SwiperSlide>
    <SwiperSlide>
      <div class="flex flex-col justify-between scroll-p-16">
        <div class="space-y-4">
          <div class="flex space-x-4 items-center">
            <NamiButton
              buttonType="secondary"
              text
              pill
              :icon="IconChevronLeft"
              @buttonClick="swiperClass?.slideTo(0)"
            />
            <span class="my-auto text-lg font-medium flex-grow sm:flex-grow-0">
              {{ selectedTier?.title }}
            </span>
            <ContextMenuList
              v-if="moreTiers && moreTiers.length > 0"
              closeOnOutsideClick
              defaultPosition="bottom-left"
              class="inline-block relative"
              :options="
                moreTiers.map((tier) => ({
                  name: tier.title,
                  action: () => (selectedTier = tier),
                })) ?? []
              "
            >
              <template v-slot="{ isOpen, open, close }">
                <NamiButton
                  @buttonClick="() => (isOpen ? close() : open())"
                  buttonType="secondary"
                  :icon="IconChevronDown"
                  :text="!isOpen"
                  iconPosition="right"
                  noWaves
                >
                  Similar tiers
                </NamiButton>
              </template>
            </ContextMenuList>
          </div>
          <div>
            <InputMoney
              :modelValue="selectedPledgeAmount / 100"
              @update:modelValue="
                (v) => (selectedPledgeAmount = v * 100).round(0)
              "
              :currency="selectedCurrency"
              :label="
                translate(
                  'components.organizationSubscribeForm.inputPledgeAmountLabel',
                )
              "
            />
            <p
              v-if="selectedPledgeAmountErrors.length > 0"
              class="text-red-500 mt-1"
            >
              {{ selectedPledgeAmountErrors.join(" ") }}
            </p>
            <div class="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
              You can subscribe to the {{ selectedTier?.title }} tier with a
              higher amount if you want. However, the rewards will remain the
              same as they are stated for this tier below.
            </div>
            <template
              v-if="
                selectedTier &&
                (selectedTier?.features.length > 0 ||
                  selectedTier.emotes.length > 0 ||
                  selectedTier.frames.length > 0)
              "
            >
              <h3 class="mt-4 mb-2 font-bold">You will get:</h3>
              <ul class="list-disc list-inside">
                <li v-for="feature in selectedTier?.features">
                  {{ feature }}
                </li>
                <li v-if="selectedTier.emotes.length">
                  <span>Emotes:</span>
                  <div class="pl-6 flex space-x-1 overflow-x-auto">
                    <EmoteImage
                      v-for="emote in selectedTier.emotes.slice(0, 10)"
                      :emote="emote"
                      size="small"
                    />
                    <div
                      v-if="selectedTier.emotes.length > 10"
                      class="bg-neutral-200 dark:bg-neutral-700 h-8 w-8 flex items-center justify-center rounded-md"
                    >
                      +{{ selectedTier.emotes.length - 10 }}
                    </div>
                  </div>
                </li>
                <li v-if="selectedTier.frames.length">
                  <span>Avatar Frames:</span>
                  <div class="pl-6 flex space-x-1 overflow-x-auto">
                    <FrameImage
                      v-for="frame in selectedTier.frames"
                      :frame="frame"
                      class="w-8 h-8"
                    />
                  </div>
                </li>
              </ul>
            </template>
            <div v-else class="mt-4 mb-2 italic">
              {{
                $t("components.organizationSubscribeForm.noTierBenefitsText")
              }}
            </div>
          </div>
        </div>
        <div class="mt-8 my-auto space-y-2">
          <div class="flex space-x-2">
            <IconLock />
            <p class="font-medium">Secure payments with Stripe</p>
          </div>
          <ul class="flex flex-wrap gap-2">
            <li v-if="isDarkMode" class="h-10 w-auto">
              <img class="h-full w-auto" src="~/assets/brands/visa-white.svg" />
            </li>
            <li v-else class="h-10 w-auto">
              <img class="h-full w-auto" src="~/assets/brands/visa-bluey.svg" />
            </li>
            <li class="h-10 w-auto">
              <img
                class="h-full w-auto"
                src="~/assets/brands/mastercard-light.svg"
              />
            </li>
            <li class="h-10 w-auto">
              <img class="h-full w-auto" src="~/assets/brands/amex.svg" />
            </li>
            <li class="h-10 w-auto">
              <img class="h-full w-auto" src="~/assets/brands/discover.png" />
            </li>
            <li class="h-10 w-auto">
              <img
                class="h-full w-auto bg-white rounded p-1 px-2"
                src="~/assets/brands/diners.png"
              />
            </li>
            <li class="h-10 w-auto">
              <img class="h-full w-auto" src="~/assets/brands/union.svg" />
            </li>
          </ul>
          <p class="text-neutral-600 dark:text-neutral-400 text-sm">
            *{{
              $t("components.organizationSubscribeForm.paymentProcessorFeeText")
            }}
          </p>
        </div>
        <div
          v-if="canChangeSubscriptionInterval"
          class="flex justify-end items-center gap-2"
        >
          <p>Daily billing interval</p>
          <NamiSwitch v-model="isDailyInterval" />
        </div>
        <div class="flex justify-center sm:justify-end mt-4">
          <NamiButton
            buttonType="primary"
            :disabled="disableSubscribeButton || isPendingAction"
            @buttonClick="
              user ? doSubscribe() : appStore?.openLoginRequiredModal()
            "
          >
            {{
              $t("components.organizationSubscribeForm.buttonSubscribeText")
            }}: ${{ selectedPledgeAmount / 100 }}
          </NamiButton>
        </div>
      </div>
    </SwiperSlide>
  </Swiper>
</template>

<script setup lang="ts">
import { Swiper as SwiperClass } from "swiper";
import { Swiper, SwiperSlide } from "swiper/vue";
import { IconChevronLeft, IconChevronDown, IconLock } from "@tabler/icons-vue";
import {
  Subscriptions,
  Tier,
  type Currency,
  Emote,
  isTier,
  Frame,
  type FrameEntity,
  type EmoteEntity,
} from "~/src/api";
import { getRelationship } from "~/src/api";

const props = defineProps<{
  orgSlug: string;
  orgId: string;
  orgName?: string;
  disabled?: boolean;
  highlightTierId?: string;
  highlightText?: string;
}>();

const currencies: Record<
  Currency,
  { sign: string; position: "before" | "after" }
> = {
  USD: {
    sign: "$",
    position: "before",
  },
  EUR: {
    sign: "€",
    position: "before",
  },
  GBP: {
    sign: "£",
    position: "before",
  },
  JPY: {
    sign: "¥",
    position: "before",
  },
  VES: {
    sign: "Bs.",
    position: "before",
  },
};

const nuxtApp = useNuxtApp();
const translate = nuxtApp.$i18n.global.t;
const authStore = nuxtApp.$auth();
const settingsStore = nuxtApp.$settings();
const appStore = nuxtApp.$app();

const canChangeSubscriptionInterval = useFeatureAccess(
  "monthlyDailySubscriptionIntervalShift",
);
const { getTierBanner } = useMediaLink();

const user = computed(() => authStore?.user);

const locale = nuxtApp.$i18n.global.locale;

const swiperClass = ref<SwiperClass>();

const { pending, data: tiers } = useAsyncData(
  `subscriptions-${props.orgId}`,
  async () => {
    const { data: tiers } = await Tier.search({
      organizationId: props.orgId,
      order: { amount: "asc" },
      currency: "USD",
      showDeleted: false,
    });

    const token = await authStore?.getToken();

    const subscribedTierIds = token
      ? await Subscriptions.getSubscriptions(token, {
          organizationId: props.orgId,
          states: ["active", "expiring", "pending"],
        }).then((subs) =>
          subs.data.map((sub) => getRelationship(sub, "tier_item").id),
        )
      : [];

    const orgEmotes = await Emote.getEmotes({
      organizationIds: [props.orgId],
      requirements: ["subscription"],
    });
    const orgFrames = await Frame.get({
      organizationIds: [props.orgId],
      requirements: ["subscription"],
    });

    return tiers.map((tier) => ({
      id: tier.id,
      title: extractFromLocale(locale.value, tier.attributes.name),
      // Only USD for now
      price: tier.attributes.minAmounts.USD!,
      features: extractFromLocale(locale.value, tier.attributes.description)
        .split("\n")
        .filter((x) => !!x),
      emotes: orgEmotes.data.filter(
        (reward) => reward.relationships.find(isTier)?.id === tier.id,
      ),
      frames: orgFrames.data.filter(
        (reward) => reward.relationships.find(isTier)?.id === tier.id,
      ),
      highlighted: false,
      banner: getTierBanner(tier, "1280"),
      subscribedToId: subscribedTierIds.at(0),
    }));
  },
);

const selectedTier = ref<{
  id: string;
  title: string;
  price: number;
  features: string[];
  emotes: EmoteEntity[];
  frames: FrameEntity[];
  highlighted: boolean;
}>();
const moreTiers = computed(() =>
  tiers.value?.filter(
    ({ price, id }) =>
      price === selectedTier.value?.price && id !== selectedTier.value.id,
  ),
);
const selectedPledgeAmount = ref(0);
const selectedCurrency = ref("USD");
const isPendingAction = ref(false);

const isDailyInterval = ref(false);

watch(selectedPledgeAmount, (newVal) => {
  if (!tiers.value) {
    return;
  }
  if (selectedTier.value?.price === newVal) {
    return;
  }

  if (newVal < tiers.value[0].price) {
    selectedTier.value = tiers.value[0];
  } else {
    selectedTier.value = tiers.value.reduce((a, b) =>
      newVal >= a.price && newVal < b.price ? a : b,
    );
  }
});

const selectedPledgeAmountErrors = computed(() => {
  const errors: string[] = [];
  if (!selectedTier.value) return errors;

  if (typeof selectedPledgeAmount.value !== "number") {
    errors.push("Invalid input type. Please input a number.");
  }

  if (selectedPledgeAmount.value < selectedTier.value.price) {
    errors.push(
      `Please input a number that is greater or equal to ${formatToCurrency(
        selectedTier.value.price / 100,
        "USD",
      )}.`,
    );
  }

  if (
    selectedPledgeAmount.value / 100 !==
      Math.floor(selectedPledgeAmount.value / 100) ||
    selectedPledgeAmount.value < 0
  ) {
    errors.push("Pledge amount can only be a positive number.");
  }

  return errors;
});

const disableSubscribeButton = computed(
  () => selectedPledgeAmountErrors.value.length > 0,
);

const isDarkMode = computed(
  () =>
    settingsStore?.settings.site.theme === "dark" ||
    (settingsStore?.settings.site.theme === "system" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches),
);

function scrollUp() {
  window.scrollTo({
    top: 256,
    behavior: "smooth",
  });
}

async function doSubscribe() {
  isPendingAction.value = true;
  const user = await authStore?.getUser();

  if (!user) {
    throw new Error("Not logged in");
  }

  try {
    const subscriptions = await Subscriptions.getSubscriptions(
      user.access_token,
      {
        organizationId: props.orgId,
        states: ["active"],
      },
    );

    if (subscriptions.data.length > 0) {
      throw new Error("You are already subscribed to this organization.");
    }

    const { redirectUri } = await Subscriptions.subscribe(
      user.access_token,
      "stripe",
      props.orgId,
      selectedTier.value!.id,
      {
        amount: selectedPledgeAmount.value,
        currency: "USD",
      },
      locale.value,
      isDailyInterval.value ? "day" : "month",
    );

    navigateTo(redirectUri, {
      external: true,
    });
  } catch (e) {
    const { title, detail } = resolveError(e);

    appStore?.notify({
      preset: "error.plain",
      title,
      detail,
      timer: 5000,
    });
  }

  isPendingAction.value = false;
}

function formatToCurrency(amount: number, currency: Currency) {
  const { sign, position } = currencies[currency];
  if (position === "before") return `${sign}${amount}`;
  else return `${amount}${sign}`;
}
</script>
